import { useCallback, useMemo } from 'react'
import { addToast } from '@heroui/react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastOptions {
  duration?: number
  placement?:
    | 'top'
    | 'top-center'
    | 'top-left'
    | 'top-right'
    | 'bottom'
    | 'bottom-center'
    | 'bottom-left'
    | 'bottom-right'
}

export interface UseToastReturn {
  toast: (message: string, type?: ToastType, options?: ToastOptions) => void
  success: (message: string, options?: ToastOptions) => void
  error: (message: string, options?: ToastOptions) => void
  warning: (message: string, options?: ToastOptions) => void
  info: (message: string, options?: ToastOptions) => void
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: unknown) => string)
    },
    options?: ToastOptions
  ) => Promise<T>
}

export const useToast = (): UseToastReturn => {
  const showToast = useCallback(
    (message: string, type: ToastType = 'info', options?: ToastOptions) => {
      const colorMap = {
        success: 'success',
        error: 'danger',
        warning: 'warning',
        info: 'primary',
      } as const

      addToast({
        title: message,
        color: colorMap[type],
        timeout: options?.duration || (type === 'success' ? 2000 : 4000),
        ...options,
      })
    },
    []
  )

  const success = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast(message, 'success', options),
    [showToast]
  )

  const error = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast(message, 'error', options),
    [showToast]
  )

  const warning = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast(message, 'warning', options),
    [showToast]
  )

  const info = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast(message, 'info', options),
    [showToast]
  )

  // Promise-based toast for async operations
  const promise = useCallback(
    async <T>(
      promise: Promise<T>,
      {
        loading,
        success: successMessage,
        error: errorMessage,
      }: {
        loading: string
        success: string | ((data: T) => string)
        error: string | ((error: unknown) => string)
      },
      options?: ToastOptions
    ) => {
      // Show loading toast
      addToast({
        title: loading,
        color: 'default',
        timeout: Infinity, // Keep loading toast until resolved
        ...options,
      })

      try {
        const data = await promise
        const message =
          typeof successMessage === 'function'
            ? successMessage(data)
            : successMessage
        showToast(message, 'success', options)
        return data
      } catch (err) {
        const message =
          typeof errorMessage === 'function' ? errorMessage(err) : errorMessage
        showToast(message, 'error', options)
        throw err
      }
    },
    [showToast]
  )

  return useMemo(
    () => ({
      toast: showToast,
      success,
      error,
      warning,
      info,
      promise,
    }),
    [showToast, success, error, warning, info, promise]
  )
}
